import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  signal,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  getCustomOptionText,
  isCustomOptionValue,
  toCustomOptionValue
} from '@shared/helpers/custom-option-value.helper';

export interface ICounterListItem {
  key: string;
  /** Text or translation key. */
  label: string;
}

export type CounterListValue = Record<string, number> | null;

interface ICounterRow {
  key: string;
  label: string;
  custom: boolean;
}

/**
 * Counts per item, e.g. rooms or windows (ControlValueAccessor, value: { [key]: count } | null).
 * Counts of 0 are removed; `allowCustom` lets the user add an own item stored as `custom:<text>`.
 */
@Component({
  selector: 'counter-list',
  templateUrl: './counter-list.component.html',
  styleUrls: ['./counter-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CounterListComponent), multi: true }
  ]
})
export class CounterListComponent implements ControlValueAccessor {
  items = input.required<ICounterListItem[]>();
  max = input<number | null>(null);
  allowCustom = input<boolean>(false);
  customLabel = input<string>('CounterList.addCustom');
  customPlaceholder = input<string>('ChipSelect.customPlaceholder');
  customMaxLength = input<number>(60);

  @ViewChild('customInput') customInput: ElementRef<HTMLInputElement>;

  protected readonly value = signal<Record<string, number>>({});
  protected readonly disabled = signal(false);
  protected readonly editingCustom = signal(false);

  protected readonly rows = computed<ICounterRow[]>(() => [
    ...this.items().map((item) => ({ key: item.key, label: item.label, custom: false })),
    ...Object.keys(this.value())
      .filter((key) => isCustomOptionValue(key))
      .map((key) => ({ key, label: getCustomOptionText(key), custom: true }))
  ]);

  private onChange: (value: CounterListValue) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: CounterListValue): void {
    this.value.set(value && typeof value === 'object' ? { ...value } : {});
  }

  registerOnChange(fn: (value: CounterListValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected count(key: string): number {
    return this.value()[key] || 0;
  }

  protected canIncrease(key: string): boolean {
    const max = this.max();
    return !this.disabled() && (max === null || this.count(key) < max);
  }

  protected change(key: string, delta: number): void {
    if (this.disabled()) return;
    this.setCount(key, this.count(key) + delta);
  }

  protected startCustom(): void {
    if (this.disabled()) return;
    this.editingCustom.set(true);
    setTimeout(() => this.customInput?.nativeElement.focus());
  }

  protected confirmCustom(): void {
    const text = this.customInput?.nativeElement.value.trim().slice(0, this.customMaxLength());
    this.editingCustom.set(false);
    if (!text) return;

    const key = toCustomOptionValue(text);
    this.setCount(key, this.count(key) + 1);
  }

  protected cancelCustom(): void {
    this.editingCustom.set(false);
  }

  private setCount(key: string, count: number): void {
    const value = { ...this.value() };
    if (count > 0) {
      value[key] = count;
    } else {
      delete value[key];
    }

    this.value.set(value);
    this.onChange(Object.keys(value).length ? value : null);
    this.onTouched();
  }
}
